import React, { useState } from 'react'
import { Modal, ModalBody, Input } from 'reactstrap'
import {
    Share2,
    Copy,
    Check,
    X,
    Mail,
    Globe,
    Smartphone,
    Tag
} from 'react-feather'
import PropTypes from 'prop-types'
import { getContrastTextColor, isLightColor } from '@utils'

const ShareProfileModal = ({ isOpen, toggle, brandData }) => {
    const [copied, setCopied] = useState(false)
    const [includeDiscount] = useState(true)

    if (!brandData) return null

    const brandName = brandData?.brand_name || brandData?.name || 'Brand'
    const brandId = brandData?.id || brandData?.profile_id || ''
    const brandLogo = brandData?.company_logo || brandData?.logo || ''
    const brandColor = brandData?.brand_color || '#56B6FF'
    const tagline = brandData?.tagline || brandData?.person_title || brandData?.contact_title || ''
    const discountCode = brandData?.discount_code || brandData?.client_discount_code || ''
    const discountDetails = brandData?.discount_details || ''

    // Light theme detection & high-contrast text color selection
    const isLight = isLightColor(brandColor)
    const headerTextColor = getContrastTextColor(brandColor)
    const headerSubTextColor = isLight ? 'rgba(30, 41, 59, 0.82)' : 'rgba(255, 255, 255, 0.92)'
    const headerBadgeBg = isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.22)'
    const headerBadgeBorder = isLight ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(255, 255, 255, 0.35)'
    const headerBadgeColor = headerTextColor
    const closeBtnBg = isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.35)'
    const closeBtnColor = isLight ? '#0f172a' : '#ffffff'

    // Shareable link for public visitors, RDs & external collaborators
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const shareUrl = brandId ? `${origin}/brand/${brandId}` : (typeof window !== 'undefined' ? window.location.href : '')

    // Formatted share text
    let shareText = `Check out ${brandName} on The RD Link!`
    if (includeDiscount && discountCode) {
        shareText += ` Exclusive Discount Code: ${discountCode}${discountDetails ? ` (${discountDetails})` : ''}.`
    }
    shareText += ` View profile: ${shareUrl}`

    const handleCopy = () => {
        if (navigator?.clipboard) {
            navigator.clipboard.writeText(shareUrl)
            setCopied(true)
            setTimeout(() => setCopied(false), 2500)
        }
    }

    const handleNativeShare = async () => {
        if (navigator?.share) {
            try {
                await navigator.share({
                    title: `${brandName} on The RD Link`,
                    text: shareText,
                    url: shareUrl
                })
            } catch (err) {
                // User cancelled or share failed
            }
        } else {
            handleCopy()
        }
    }

    // Social share links
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    const emailUrl = `mailto:?subject=${encodeURIComponent(`${brandName} on The RD Link`)}&body=${encodeURIComponent(shareText)}`

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            className="modal-dialog-centered"
            contentClassName="border-0 shadow-lg"
            style={{ maxWidth: '460px', borderRadius: '18px', overflow: 'hidden' }}
        >
            <ModalBody className="p-0 position-relative" style={{ backgroundColor: '#ffffff' }}>
                {/* Close Button */}
                <button
                    type="button"
                    onClick={toggle}
                    className="btn btn-sm p-0 position-absolute d-flex align-items-center justify-content-center"
                    style={{
                        top: '12px',
                        right: '12px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: closeBtnBg,
                        color: closeBtnColor,
                        zIndex: 10,
                        border: isLight ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                        backdropFilter: 'blur(4px)',
                        transition: 'all 0.2s ease'
                    }}
                    title="Close"
                >
                    <X size={16} />
                </button>

                {/* Top Profile Snapshot Card */}
                <div
                    style={{
                        backgroundColor: brandColor,
                        backgroundImage: isLight
                            ? 'linear-gradient(180deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.08) 100%)'
                            : 'linear-gradient(180deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.35) 100%)',
                        borderBottom: isLight ? '1px solid #e2e8f0' : 'none',
                        padding: '28px 20px 20px 20px',
                        textAlign: 'center',
                        color: headerTextColor,
                        position: 'relative'
                    }}
                >
                    {/* Circle Logo Avatar */}
                    <div
                        style={{
                            width: '72px',
                            height: '72px',
                            borderRadius: '50%',
                            backgroundColor: '#ffffff',
                            margin: '0 auto 10px auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: isLight ? '0 4px 14px rgba(0, 0, 0, 0.12)' : '0 6px 18px rgba(0, 0, 0, 0.25)',
                            border: isLight ? '2px solid #cbd5e1' : '3px solid #ffffff',
                            overflow: 'hidden'
                        }}
                    >
                        {brandLogo ? (
                            <img
                                src={brandLogo}
                                alt={brandName}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <span style={{ fontSize: '22px', fontWeight: 800, color: isLight ? '#0f172a' : brandColor }}>
                                {brandName.substring(0, 3).toUpperCase()}
                            </span>
                        )}
                    </div>

                    <h4
                        className="font-weight-bolder mb-25"
                        style={{
                            fontSize: '18px',
                            letterSpacing: '-0.2px',
                            color: headerTextColor
                        }}
                    >
                        {brandName}
                    </h4>

                    {tagline && (
                        <p
                            className="mb-50 font-small-2 text-truncate"
                            style={{
                                maxWidth: '85%',
                                margin: '0 auto',
                                color: headerSubTextColor
                            }}
                        >
                            {tagline}
                        </p>
                    )}

                    <div className="d-flex align-items-center justify-content-center mt-50" style={{ gap: '6px' }}>
                        {/* RD Link Profile Badge */}
                        <span
                            className="badge px-1 py-25"
                            style={{
                                fontSize: '10.5px',
                                borderRadius: '12px',
                                background: headerBadgeBg,
                                color: headerBadgeColor,
                                border: headerBadgeBorder
                            }}
                        >
                            <Globe size={11} className="mr-25" style={{ verticalAlign: '-1px' }} /> The RD Link Profile
                        </span>

                        {/* Discount Code Badge - Matching Background & Border */}
                        {discountCode && (
                            <span
                                className="badge px-1 py-25 font-weight-bold"
                                style={{
                                    fontSize: '10.5px',
                                    borderRadius: '12px',
                                    background: headerBadgeBg,
                                    color: headerBadgeColor,
                                    border: headerBadgeBorder
                                }}
                            >
                                <Tag size={11} className="mr-25" style={{ verticalAlign: '-1px' }} /> Code: {discountCode}
                            </span>
                        )}
                    </div>
                </div>

                {/* Body Content */}
                <div className="p-2">
                    {/* Share Modal Header Title */}
                    <div className="d-flex align-items-center mb-1" style={{ gap: '8px' }}>
                        <div
                            style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #7367F0 0%, #CE9FFC 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ffffff',
                                flexShrink: 0
                            }}
                        >
                            <Share2 size={16} />
                        </div>
                        <div>
                            <h5 className="mb-0 font-weight-bold" style={{ fontSize: '15px', color: '#0f172a' }}>
                                Share Brand Profile
                            </h5>
                            <span className="font-small-1" style={{ color: '#64748b' }}>
                                Share with dietitians, clients & partners
                            </span>
                        </div>
                    </div>

                    {/* Standard Social Share Grid */}
                    <div className="d-flex align-items-center justify-content-between my-1 flex-wrap" style={{ gap: '10px' }}>
                        {/* Native / Web Share */}
                        {typeof navigator !== 'undefined' && !!navigator.share && (
                            <button
                                type="button"
                                onClick={handleNativeShare}
                                className="btn btn-sm p-0 d-flex flex-column align-items-center justify-content-center"
                                style={{ border: 'none', background: 'transparent', flex: '1 0 54px' }}
                                title="Device Share"
                            >
                                <div
                                    style={{
                                        width: '46px',
                                        height: '46px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #7367F0 0%, #9E95F5 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#ffffff',
                                        boxShadow: '0 3px 10px rgba(115, 103, 240, 0.3)',
                                        marginBottom: '6px'
                                    }}
                                >
                                    <Smartphone size={20} />
                                </div>
                                <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>Share</span>
                            </button>
                        )}

                        {/* WhatsApp */}
                        <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="d-flex flex-column align-items-center justify-content-center text-decoration-none"
                            style={{ flex: '1 0 54px' }}
                            title="Share on WhatsApp"
                        >
                            <div
                                style={{
                                    width: '46px',
                                    height: '46px',
                                    borderRadius: '50%',
                                    background: '#25D366',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#ffffff',
                                    boxShadow: '0 3px 10px rgba(37, 211, 102, 0.3)',
                                    marginBottom: '6px'
                                }}
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                                </svg>
                            </div>
                            <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>WhatsApp</span>
                        </a>

                        {/* X (Twitter) */}
                        <a
                            href={twitterUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="d-flex flex-column align-items-center justify-content-center text-decoration-none"
                            style={{ flex: '1 0 54px' }}
                            title="Share on X"
                        >
                            <div
                                style={{
                                    width: '46px',
                                    height: '46px',
                                    borderRadius: '50%',
                                    background: '#0f172a',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#ffffff',
                                    boxShadow: '0 3px 10px rgba(15, 23, 42, 0.25)',
                                    marginBottom: '6px'
                                }}
                            >
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                </svg>
                            </div>
                            <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>X (Twitter)</span>
                        </a>

                        {/* LinkedIn */}
                        <a
                            href={linkedinUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="d-flex flex-column align-items-center justify-content-center text-decoration-none"
                            style={{ flex: '1 0 54px' }}
                            title="Share on LinkedIn"
                        >
                            <div
                                style={{
                                    width: '46px',
                                    height: '46px',
                                    borderRadius: '50%',
                                    background: '#0A66C2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#ffffff',
                                    boxShadow: '0 3px 10px rgba(100, 116, 139, 0.3)',
                                    marginBottom: '6px'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                </svg>
                            </div>
                            <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>LinkedIn</span>
                        </a>

                        {/* Facebook */}
                        <a
                            href={facebookUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="d-flex flex-column align-items-center justify-content-center text-decoration-none"
                            style={{ flex: '1 0 54px' }}
                            title="Share on Facebook"
                        >
                            <div
                                style={{
                                    width: '46px',
                                    height: '46px',
                                    borderRadius: '50%',
                                    background: '#1877F2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#ffffff',
                                    boxShadow: '0 3px 10px rgba(24, 119, 242, 0.3)',
                                    marginBottom: '6px'
                                }}
                            >
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M9 8H6v4h3v12h5V12h3.642L18 8h-4V6.333C14 5.374 14.5 5 15.5 5H18V0h-3.808C10.593 0 9 1.582 9 4.615V8z" />
                                </svg>
                            </div>
                            <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>Facebook</span>
                        </a>

                        {/* Email */}
                        <a
                            href={emailUrl}
                            className="d-flex flex-column align-items-center justify-content-center text-decoration-none"
                            style={{ flex: '1 0 54px' }}
                            title="Share via Email"
                        >
                            <div
                                style={{
                                    width: '46px',
                                    height: '46px',
                                    borderRadius: '50%',
                                    background: '#64748B',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#ffffff',
                                    boxShadow: '0 3px 10px rgba(100, 116, 139, 0.3)',
                                    marginBottom: '6px'
                                }}
                            >
                                <Mail size={20} />
                            </div>
                            <span style={{ fontSize: '11px', color: '#334155', fontWeight: 600 }}>Email</span>
                        </a>
                    </div>

                    {/* Copy Link Input Bar */}
                    <div className="mt-1 p-1 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <label className="font-small-2 font-weight-bold text-uppercase mb-50 d-block" style={{ color: '#475569' }}>
                            Page URL
                        </label>
                        <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                            <Input
                                type="text"
                                readOnly
                                value={shareUrl}
                                onClick={(e) => e.target.select()}
                                className="bg-white font-small-2 font-weight-bold"
                                style={{ height: '38px', borderRadius: '8px', borderColor: '#cbd5e1', color: '#0f172a' }}
                            />
                            <button
                                type="button"
                                onClick={handleCopy}
                                className={`btn ${copied ? 'btn-success' : 'btn-primary'} d-inline-flex align-items-center justify-content-center px-1`}
                                style={{
                                    height: '38px',
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    fontSize: '12.5px',
                                    flexShrink: 0,
                                    minWidth: '95px',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {copied ? (
                                    <>
                                        <Check size={14} className="mr-25" /> Copied!
                                    </>
                                ) : (
                                    <>
                                        <Copy size={14} className="mr-25" /> Copy Link
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    )
}

ShareProfileModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    brandData: PropTypes.object
}

export default ShareProfileModal
